import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useEffect, useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import GroupButtons, { type ButtonOfGroup } from '@/components/GroupButtons';
import PageHeader from '@/components/PageHeader';
import { useDirtyForm } from '@/context/dirtyForm.context';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import ModalHistorialUsuario from '@/pages/Administrar/Usuario/ModalHistorialUsuario';
import UserForm from '@/pages/Administrar/Usuario/UserForm';
import {
	defaultValues,
	resolver,
	schema,
	type IUserForm,
	type NotificacionTramiteForm,
} from '@/pages/Administrar/Usuario/UserForm/config';
import { getUsuario, postUsuario, putUsuarioEstadoActivo, type IUsuarioPayload } from '@/services/usuarios.service';

import { ERROR_TYPE } from '@/constants/errors.constants';
import { getLookup } from '@/helpers/dropdown';
import { notificationsConfig } from '@/helpers/notifications';
import { cleanObj } from '@/helpers/objects';
import { groupByTipoTramite } from '@/helpers/tipoTramite';
import { peekErrors } from '@/helpers/validations';
import { type ILookup } from '@/interfaces/model/generic.interface';
import { type IMaestro } from '@/interfaces/model/maestros.interface';
import {type NotificacionTramite } from '@/interfaces/model/user.interface';

import { ROUTES, ROUTES_LABEL } from '../../../constants/routes.constants';

const parsedErrors = [
	{ backError: 'Ya existe un usuario registrado con el username indicado.',
		error : 'Ya existe un usuario registrado con el usuario indicado.'
	}, 
	{ backError: 'Ya existe un usuario registrado con el email indicado.',
		error: 'Ya existe un usuario registrado con el email indicado.'}];

const Usuario = (): ReactElement => {
	const { appendBreadcrumb, setUrlBack } = usePageContext();
	const navigate = useNavigate();
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const [hasMissingRequired, setHasMissingRequired] = useState(true);
	const [isSameUser, setIsSameUser] = useState(false);
	const modalHistorial = useModal({});
	const methods = useForm<IUserForm>({ defaultValues, resolver: resolver(), mode: 'onSubmit' });
	const {
		formState: { dirtyFields, isDirty },
		watch,
		setValue,
		reset,
		handleSubmit,
		getValues,
	} = methods;
	const envioNotificacionMail = watch('envioNotificacionMail');
	const { triggerFetch, loading } = useApiData({ loading: ['save', 'load', 'activo'] as const });
	const { setIsDirtyForm, checkDirtyForm } = useDirtyForm();
	const {
		maestrosGeneric: { tipoNotificacionTramite, rol: rolMaestro },
		user,
	} = useGlobalContext();
	const { id } = useParams();

	const isNew = !id;

	const onCancel = () => {
		checkDirtyForm({
			action() {
				navigate(ROUTES.ADMINISTRAR, { state: { activeTab: 'usuarios' } });
			},
		});
	};

	const onSubmit = async (values: IUserForm) => {
		const { rol, serviciosNotificaciones, username, nombre, apellido1, apellido2, email, telefono } =
			values;
		const rolLookup = getLookup(rol, rolMaestro);

		// Enviar los trámites habilitados
		const listaAcciones = serviciosNotificaciones
			.filter(({ tipoTramite }) => tipoTramite.value.includes('true'))
			.map(({ tipoTramite }) => ({ id: tipoTramite.id }));
		
		if (listaAcciones.length === 0) {
			openNotification(notificationTemplate.accionIndebidaUser());
			return;
		}

		// Filtrar todos los checkbox habilitados
		// Se descarta aquellos servicios que no tengan notificaciones
		const listaTiposNotificacionTramiteMapped: NotificacionTramite[] = serviciosNotificaciones
			.filter(({ tipoTramite, listaTipoNotificacion }) => {
				const listaTipoNotificacionFiltered = listaTipoNotificacion.filter(({ value }) =>
					value.includes('true'),
				);
				return tipoTramite.value.includes('true') && listaTipoNotificacionFiltered.length > 0;
			})
			.map(({ listaTipoNotificacion, tipoTramite }) => {
				const listaTipoNotificacionFiltered = listaTipoNotificacion
					.filter(({ value }) => value.includes('true'))
					.map(({ value, ...rest }) => rest);

				// Quitar el value, que es un campo añadido para controlar el valor del check
				const { value, ...rest } = tipoTramite;

				return { tipoTramite: rest, listaTipoNotificacion: listaTipoNotificacionFiltered };
			});

		const payload: IUsuarioPayload = {
			nombre,
			username,
			apellido1,
			apellido2,
			email,
			telefono,
			id: isNaN(Number(id)) ? null : Number(id),
			listaAcciones,
			listaTiposNotificacionTramite: listaTiposNotificacionTramiteMapped,
			envioNotificacionMail: envioNotificacionMail?.includes('1'),
			rol: isSameUser ? undefined : rolLookup as IMaestro,
		};

		const response = await triggerFetch({
			payload: cleanObj(payload),
			name: 'save',
			fetch: postUsuario,
		});

		if (response.code === 200) {
			openNotification(notificationTemplate.successMessage('Confirmación', 'Usuario guardado correctamente'));
			navigate(ROUTES.ADMINISTRAR, { state: { activeTab: 'usuarios' } });
			return;
		}

		if (parsedErrors.flatMap((it) => it.backError).includes(response.message ?? '')) {
			const error = parsedErrors.find((it) => it.backError === response.message)?.error;
			openNotification(notificationTemplate.error406({message: error ?? '', errores: []}));
			return;
		}

		openNotification(notificationTemplate.errorUser());
	};

	const loadDefaultData = () => {
		const serviciosNotificaciones: NotificacionTramiteForm[] = groupByTipoTramite(tipoNotificacionTramite).map(
			({ tipoTramite, items }) => ({
				tipoTramite: { ...tipoTramite, value: [] },
				listaTipoNotificacion: items.map(({ tipoNotificacion }) => ({ ...tipoNotificacion, value: [] })),
			}),
		);
		reset({ ...defaultValues, serviciosNotificaciones });
	};

	const mapListaTipoNotificacion = (tipoNotificacion: ILookup, tipoTramiteMatch?: NotificacionTramite) => {
		const tipoNotificacionMatch = tipoTramiteMatch
			? tipoTramiteMatch.listaTipoNotificacion.find((data) => data.id === tipoNotificacion.id)
			: undefined;
		return { ...tipoNotificacion, value: tipoNotificacionMatch ? ['true'] as Array<'true'> : [] };
	};

	const loadDataByID = async (idUser: number) => {
		const { data } = await triggerFetch({
			fetch: getUsuario,
			name: 'load',
			payload: idUser,
			error: 'Se ha producido un error al recuperar el usuario.',
		});

		if (!data) return;

		const { activo, rol, listaTiposNotificacionTramite, listaAcciones, username, nifEmisor } = data;
			
		setIsSameUser(user?.nifEmisor === nifEmisor && user?.username === username);

		const serviciosNotificaciones: NotificacionTramiteForm[] = groupByTipoTramite(tipoNotificacionTramite).map(
			({ tipoTramite, items }) => {
				const tipoTramiteMatch = listaTiposNotificacionTramite.find(
					(data) => data.tipoTramite.id === tipoTramite.id,
				);
				const tipoActionMatch = listaAcciones.find((it) => it.tipoTramite?.id === tipoTramite.id);

				return {
					tipoTramite: { ...tipoTramite, value: tipoActionMatch ? ['true']  : [] },
					listaTipoNotificacion: items.map(({ tipoNotificacion }) => mapListaTipoNotificacion(tipoNotificacion, tipoTramiteMatch)),
				};
			},
		);
		reset({
			...data,
			id: data.id ?? 0,
			activo: activo ? ['true'] : [],
			rol: String(rol.id),
			serviciosNotificaciones,
			envioNotificacionMail: data.envioNotificacionMail ? ['1'] : []
		});
	};

	const onChangeActivo = async (value: Array<string | number>) => {
		const { serviciosNotificaciones } = getValues();
		const listaAcciones = serviciosNotificaciones
			.filter(({ tipoTramite }) => tipoTramite.value.includes('true'))
			.map(({ tipoTramite }) => ({ id: tipoTramite.id }));
		
		if (listaAcciones.length === 0) {
			openNotification(notificationTemplate.accionBloqueadaUser());			
			setValue('activo', value.includes('true') ? [] : ['true']);			
			return;
		}

		const { code } = await triggerFetch({
			fetch: putUsuarioEstadoActivo,
			name: 'activo',
			payload: Number(id),
			error: 'Error al activar/desactivar el usuario.',
		});

		if (code !== 200) {
			// Revertir cambio
			setValue('activo', value.includes('true') ? [] : ['true']);
		}
	};

	// Si se encuentra algún campo dirty se activará la modal de cambios pendientes cuando salgamos de la ruta.^
	// Excluir campo activo, ya que tiene su propio servicio
	useEffect(() => {
		const isDirty = Object.entries(dirtyFields)
			.filter(([key]) => key !== 'activo')
			.some(([_, value]) => Boolean(value));
		setIsDirtyForm(isDirty);
	}, [Object.values(dirtyFields)]);

	// Deshabilitar el botón si hay algun campo obligatorio sin rellenar.
	useEffect(() => {
		const check = async () => {
			const errors = await peekErrors({
				schema,
				data: watch(),
			});

			const hasErrorsRequired = errors.some(({ type }) => type === ERROR_TYPE.required);
			setHasMissingRequired(hasErrorsRequired);
		};

		check();
	}, [watch(), schema]);

	useEffect(() => {
		appendBreadcrumb(isNew ? ROUTES_LABEL.USUARIO_NUEVO : ROUTES_LABEL.USUARIO_EDIT, [
			{ url: ROUTES.ADMINISTRAR, label: ROUTES_LABEL.ADMINISTRAR },
			{ label: isNew ? ROUTES_LABEL.USUARIO_NUEVO : ROUTES_LABEL.USUARIO_EDIT, url: location.pathname },
		]);

		if (id) {
			loadDataByID(Number(id));
		} else {
			loadDefaultData();
		}
		
		setUrlBack({
			show: true,
			label: 'Volver',
			navigate() {
				navigate(ROUTES.ADMINISTRAR, { state: { activeTab: 'usuarios' } });
			}
		});
	}, []);

	return (
		<div className="p-usuario">
			<PageHeader
				hasHelp
				title={isNew ? ROUTES_LABEL.USUARIO_NUEVO : ROUTES_LABEL.USUARIO_EDIT}
				className="u-mt-8"
			/>
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-flow-border ]  [ u-mt-24 ]">
				<div className="c-pnp-box__body p-confirm">
					<UserForm
						methods={methods}
						isSameUser={isSameUser}
						show={{ activo: !isNew }}
						disabled={{ username: !isNew, activo: loading.activo }}
						onChange={{ 
							activo(valor) { 
								onChangeActivo(valor); 
							} 
						}}
					/>
				</div>
				<footer className="c-pnp-box__footer">
					<GroupButtons
						left={[
							...(isNew
								? []
								: [
									{
										id: 'historial',
										variant: 'outline',
										mode: 'secondary',
										minWidth: '140',
										label: 'Ver historial',
										onClick: modalHistorial.openModal,
									} satisfies ButtonOfGroup,
								]),
						]}
						right={[
							(isNew
								? {
									id: 'add',
									mode: 'primary',
									minWidth: '140',
									disabled: hasMissingRequired || !isDirty,
									label: 'Añadir usuario',
									loading: loading.save,
									onClick(e) {
										handleSubmit(onSubmit)(e);
									}
								}
								: {
									id: 'save',
									mode: 'primary',
									minWidth: '140',
									disabled: hasMissingRequired || !isDirty,
									loading: loading.save,
									label: 'Guardar',
									onClick(e) {
										handleSubmit(onSubmit)(e);
									}
								}) satisfies ButtonOfGroup,
							{
								id: 'cancel',
								mode: 'tertiary',
								minWidth: '140',
								label: 'Cancelar',
								onClick: onCancel,
							},
						]}
					/>
				</footer>
			</div>
			<ModalHistorialUsuario
				isOpen={modalHistorial.isShowing}
				data={watch()}
				onClose={modalHistorial.closeModal}
			/>
		</div>
	);
};

export default Usuario;
